import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useStore } from "../../../stores/store";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FilterPanel from "./FilterPanel";
import useAuth from '../../../hooks/useAuth';
import { observer } from "mobx-react-lite";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { Row, Col, Card, Table, Button, Badge } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { PageHeader, StyledCard, EmptyState } from '../../Common/UI';
import { format } from 'date-fns';
import './ExamRegistration.css';

const Index = observer(() => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const { mainStore } = useStore();

  useSetMenu(mainStore.ExamRegistrationMenuType, mainStore.ExamRegistrationMenuNo);

  const { register, setValue, formState: { errors } } = useForm();

  const [filters, setFilters] = useState({
    academicYearId: null,
    termRegistrationId: null,
    subjectId: null,
    examDate: '',
  });

  const [examRegistrations, setExamRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState({});
  const [registeringIds, setRegisteringIds] = useState(new Set());
  const [selectedTermRegistration, setSelectedTermRegistration] = useState(null);
  const [isWithinDateRange, setIsWithinDateRange] = useState(true);

  const universityId = auth?.universityId;

  const loadGradeOptions = async () => {
    if (!universityId) return;

    try {
      const response = await axiosPrivate.get(
        `/StaffPortalExamRegistrations/GetGradeOptionsForExam/${universityId}`
      );

      const options = response.data.map(item => {
        return {
          value: item.value,
          label: item.label,
          gradeNumber: item.gradeNumber,
          gradeLetter: item.gradeLetter,
        };
      });

      setGradeOptions(options);
    } catch (error) {
      console.error("Error loading grade options:", error);
    }
  };

  useEffect(() => {
    loadGradeOptions();
  }, [universityId]);

  const loadExamRegistrations = async () => {
    if (!filters.termRegistrationId || !filters.subjectId) {
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
      if (filters.termRegistrationId) params.append('termRegistrationId', filters.termRegistrationId);
      if (filters.subjectId) params.append('subjectId', filters.subjectId);

      const response = await axiosPrivate.get(
        `/StaffPortalExamRegistrations/MySubjectRegistrations?${params.toString()}`
      );
      console.log('Exam registrations received:', response.data);
      setExamRegistrations(response.data);

      setSelectedGrades({});
    } catch (error) {
      console.error("Error loading exam registrations:", error);
      enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.termRegistrationId && filters.subjectId) {
      loadExamRegistrations();
    }
  }, [filters.termRegistrationId, filters.subjectId]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleTermRegistrationChange = (termRegistrationData) => {
    setSelectedTermRegistration(termRegistrationData);

    if (termRegistrationData && termRegistrationData.startDate && termRegistrationData.endDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const startDate = new Date(termRegistrationData.startDate);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(termRegistrationData.endDate);
      endDate.setHours(23, 59, 59, 999);

      const withinRange = today >= startDate && today <= endDate;
      setIsWithinDateRange(withinRange);

      if (!withinRange) {
        console.log('Current date is outside term registration date range', {
          today: today.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      }
    } else {
      setIsWithinDateRange(true);
    }
  };

  const handleGradeChange = (registrationId, selectedOption) => {
    setSelectedGrades(prev => ({
      ...prev,
      [registrationId]: selectedOption ? selectedOption.value : null
    }));
  };

  const handleRegisterGrade = async (registration) => {
    const selectedGradeId = selectedGrades[registration.id];

    if (!selectedGradeId) {
      enqueueSnackbar(t("pleaseSelectValidGrade"), { variant: "warning" });
      return;
    }

    if (!filters.examDate) {
      enqueueSnackbar(t("pleaseSelectExamDate") || "Please select exam date", { variant: "warning" });
      return;
    }

    setRegisteringIds(prev => new Set(prev).add(registration.id));

    try {
      console.log('Registering grade for:', registration);
      console.log('Selected grade ID:', selectedGradeId);

      let gradeValue = 0;
      let gradeLetter = "";
      let gradePercentage = "";
      let statusId = 1;

      const selectedOption = gradeOptions.find(opt => opt.value === selectedGradeId);
      console.log('Found selected option:', selectedOption);

      if (selectedOption) {
        gradeValue = selectedOption.gradeNumber;
        gradeLetter = selectedOption.gradeLetter;

        const gradeSystemId = parseInt(selectedGradeId);

        if (gradeSystemId === 2) {
          statusId = 5;
        } else if (gradeSystemId === 3) {
          statusId = 4;
        } else if (gradeSystemId >= 5 && gradeSystemId <= 10) {
          statusId = 1;
        } else {
          statusId = 1;
        }
      } else {
        enqueueSnackbar(t("pleaseSelectValidGrade"), { variant: "error" });
        return;
      }

      const updateData = {
        id: registration.id,
        studentId: registration.studentId,
        studentRegistrationId: registration.studentRegistrationId || registration.studentId,
        idStudent: registration.idStudent || "",
        subjectId: registration.subjectId,
        staffId: registration.staffId,
        termRegistrationId: registration.termRegistrationId,
        studentRegistrationDate: registration.studentRegistrationDate || format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
        examDate: filters.examDate,
        grade: gradeValue,
        gradeLetter: gradeLetter,
        gradePercentage: gradePercentage,
        gradeSystemId: selectedGradeId ? parseInt(selectedGradeId) : null,
        examResultStatusTypeId: statusId,
        rejectedBy: registration.rejectedBy || null,
        rejectionDate: registration.rejectionDate || null,
        universityId: registration.universityId || universityId,
        isDeleted: false,
        isActive: true
      };

      console.log('Update data being sent:', updateData);

      await axiosPrivate.put(
        `/StaffPortalExamRegistrations/UpdateGrade/${registration.id}`,
        updateData
      );

      enqueueSnackbar(t("gradeRegisteredSuccessfully") || "Grade registered successfully", { variant: "success" });

      setExamRegistrations(prev => prev.filter(r => r.id !== registration.id));

      setSelectedGrades(prev => {
        const newGrades = { ...prev };
        delete newGrades[registration.id];
        return newGrades;
      });
    } catch (error) {
      console.error('Error registering grade:', error);
      console.error('Error response:', error.response?.data);

      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
        enqueueSnackbar(`Validation errors: ${errorMessages}`, { variant: "error" });
      } else if (typeof error.response?.data === 'string') {
        enqueueSnackbar(error.response.data, { variant: "error" });
      } else {
        enqueueSnackbar(t("errorUpdatingGrade"), { variant: "error" });
      }
    } finally {
      setRegisteringIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(registration.id);
        return newSet;
      });
    }
  };

  return (
    <div className="exam-registration-container">
      <PageHeader
        icon="journal-check"
        title={t('registerGrades')}
        subtitle={t('enterStudentGradesAndResults')}
      />

      {}
      <StyledCard className="mb-4 filter-card" hoverable={false}>
        <Card.Body>
          <h6 className="filter-title mb-3">
            <i className="bi bi-funnel-fill me-2"></i>
            {t('filterOptions')}
          </h6>
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onTermRegistrationChange={handleTermRegistrationChange}
            universityId={auth.universityId}
            onShowStudents={loadExamRegistrations}
            loading={loading}
            register={register}
            errors={errors}
            setValue={setValue}
          />
        </Card.Body>
      </StyledCard>

      {}
      <StyledCard hoverable={false}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="results-title mb-0">
              <i className="bi bi-people-fill me-2"></i>
              {t('studentsList')}
            </h6>
            <Badge bg="secondary" className="results-count">
              {examRegistrations.length} {t('studentsToGrade')}
            </Badge>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
              <p className="mt-3 text-muted">{t('loadingStudents')}</p>
            </div>
          ) : examRegistrations.length > 0 ? (
            <>
              {!isWithinDateRange && selectedTermRegistration && (
                <div className="alert alert-warning mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>{t("gradeRegistrationNotAllowed")}</strong>
                  <br />
                  {t("gradeRegistrationClosedMessage", {
                    endDate: selectedTermRegistration.endDate ? new Date(selectedTermRegistration.endDate).toLocaleDateString('en-GB') : ''
                  })}
                </div>
              )}
              <div className="table-responsive">
                <Table className="grade-table" hover>
                  <thead>
                    <tr>
                      <th>{t("studentId")}</th>
                      <th>{t("studentName")}</th>
                      <th>{t("subject")}</th>
                      <th className="text-center" width="300">{t("selectGrade")}</th>
                      <th className="text-center" width="150">{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {examRegistrations.map((registration) => {
                      const isRegistering = registeringIds.has(registration.id);
                      const selectedGradeValue = selectedGrades[registration.id];
                      const selectedGradeOption = gradeOptions.find(opt => opt.value === selectedGradeValue);

                      return (
                        <tr key={registration.id}>
                          <td>
                            <span className="student-id">{registration.idStudent}</span>
                          </td>
                          <td>
                            <div className="student-info">
                              <i className="bi bi-person-circle me-2 text-muted"></i>
                              <span>{registration.studentFirstName} {registration.studentLastName}</span>
                            </div>
                          </td>
                          <td>
                            <span className="subject-name">{registration.subjectName}</span>
                          </td>
                          <td className="text-center">
                            <Select
                              value={selectedGradeOption || null}
                              onChange={(selectedOption) => handleGradeChange(registration.id, selectedOption)}
                              options={gradeOptions}
                              placeholder={t("selectGrade")}
                              isClearable={false}
                              isSearchable={false}
                              isDisabled={isRegistering || !isWithinDateRange}
                              menuPortalTarget={document.body}
                              className="grade-select"
                              classNamePrefix="grade-select"
                              styles={{
                                container: (provided) => ({ ...provided, minWidth: 250 }),
                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                              }}
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              variant="success"
                              size="sm"
                              className="register-btn"
                              onClick={() => handleRegisterGrade(registration)}
                              disabled={!selectedGradeValue || !filters.examDate || isRegistering || !isWithinDateRange}
                            >
                              {isRegistering ? (
                                <>
                                  <CircularProgress size={16} sx={{ color: 'inherit' }} className="me-2" />
                                  {t("registering") || "Registering..."}
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-check-circle me-2"></i>
                                  {t("register")}
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            filters.termRegistrationId && filters.subjectId && !loading && (
              <EmptyState
                icon="clipboard-x"
                title={t('noPendingStudentsFound')}
                message={t('allGradesHaveBeenRegistered')}
              />
            )
          )}
        </Card.Body>
      </StyledCard>
    </div>
  );
});

export default Index;